import type { EntityHelper } from "../configurator/entity-helper";
import type { ModuleHelper } from "../configurator/module-helper";
import { Configuration, Entity, Module, Product, Project, User } from "../types/type_def";

/**
 * What the user is currently doing with a dragged product.
 */
export enum ACTIONS {
    NONE,
    DRAGOUT,
    DRAGOVER
}

/**
 * UI actions the 3D/configurator code needs to trigger.
 *
 * The configurator code never touches the DOM itself; it calls these instead.
 * Implemented in app.ts.
 */
export interface AppUi {
    showLoadingCanvas(): void;
    hideLoadingCanvas(): void;
    /** Lets the user pick a placement (back/center/front) for an arm. Resolves undefined when there is no choice. */
    createEntityPlacementPopup(entity: Entity, matchingConnectors: string[]): Promise<unknown>;
    /** Runs after the module changed: thumbnail, price, validation, save. */
    handleModuleUpdating(callback?: () => void): void;
}

/**
 * Shared application state.
 *
 * Everything that used to be a top-level variable inside the app.ts closure lives here,
 * so code can be moved into separate modules and still read/write the same state.
 * Babylon objects in here must never be made Vue-reactive.
 */
export interface AppContext {

    // Scene
    canvas: HTMLCanvasElement;
    engine: BABYLON.Engine;
    scene: BABYLON.Scene;
    mainCamera: BABYLON.ArcRotateCamera;
    renderTargetCamera: BABYLON.ArcRotateCamera;
    topviewCamera: BABYLON.ArcRotateCamera;
    light: BABYLON.ShadowLight;
    light2: BABYLON.ShadowLight;
    sg: BABYLON.ShadowGenerator;
    sg2: BABYLON.ShadowGenerator;
    ground: BABYLON.Mesh;
    ground2: BABYLON.Mesh;

    rootNode: BABYLON.TransformNode;
    modulesNode: BABYLON.TransformNode;
    productsNode: BABYLON.TransformNode;
    instanceNode: BABYLON.TransformNode;

    GUI: BABYLON.GUI.AdvancedDynamicTexture;

    // Current selection
    currentModule: Module;
    currentProduct: Product;
    currentEntityNode: BABYLON.TransformNode;
    currentConfiguration: Configuration;
    currentProject: Project;
    currentModuleNode: BABYLON.TransformNode;
    currentConnector: BABYLON.TransformNode;
    currentPage: string;
    previousPage: string;

    isNewConfig: boolean;
    isSeriesConstrained: boolean;
    seriesConstrainedId: number;

    loggedIn: boolean;
    render: boolean;
    rendererInitialized: boolean;

    // Interaction
    isDragging: boolean;
    isPlacing: boolean;
    ghostEntityNode: BABYLON.TransformNode;
    ghostConnectorUniqueId: string | number;
    ghostCachedEntityUniqueId: number;
    ghostChildConnectorMesh: BABYLON.AbstractMesh;
    isOverCanvas: boolean;
    isB2C: boolean;
    meshLoading: boolean;
    resetPassword: boolean;

    // Settings
    scaleFactor: number;
    connectorNodeSize: number;
    defaultMinCameraRadius: number;
    initialCameraRadius: number;

    legConnections: any[];

    currentAction: ACTIONS;

    user: User;
    viewMode: string;

    // Animations
    animRotation: BABYLON.Animatable;
    animFitviewTarget: BABYLON.Animatable;
    animFitviewRadius: BABYLON.Animatable;

    // Pointer / camera
    dragPosition: BABYLON.Vector3;
    scenePointerPosition: BABYLON.Vector3;
    screenPointerPosition: BABYLON.Vector2;
    cameraTarget: BABYLON.Vector3;
    targetRotation: BABYLON.Vector3;
    easingFunction: BABYLON.EasingFunction;

    // Services
    moduleHelper: ModuleHelper;
    entityHelper: EntityHelper;
    ui: AppUi;
}

/**
 * Creates the context with the same start values the old top-level variables had.
 */
export function createAppContext(): AppContext {
    return {
        canvas: undefined,
        engine: undefined,
        scene: undefined,
        mainCamera: undefined,
        renderTargetCamera: undefined,
        topviewCamera: undefined,
        light: undefined,
        light2: undefined,
        sg: undefined,
        sg2: undefined,
        ground: undefined,
        ground2: undefined,

        rootNode: undefined,
        modulesNode: undefined,
        productsNode: undefined,
        instanceNode: undefined,

        GUI: undefined,

        currentModule: undefined,
        currentProduct: undefined,
        currentEntityNode: undefined,
        currentConfiguration: undefined,
        currentProject: undefined,
        currentModuleNode: undefined,
        currentConnector: undefined,
        currentPage: undefined,
        previousPage: undefined,

        isNewConfig: undefined,
        isSeriesConstrained: undefined,
        seriesConstrainedId: undefined,

        loggedIn: false,
        render: true,
        rendererInitialized: false,

        isDragging: false,
        isPlacing: false,
        ghostEntityNode: null,
        ghostConnectorUniqueId: null,
        ghostCachedEntityUniqueId: null,
        ghostChildConnectorMesh: null,
        isOverCanvas: false,
        isB2C: false,
        meshLoading: false,
        resetPassword: false,

        scaleFactor: 0.01,
        connectorNodeSize: 0.2,
        defaultMinCameraRadius: 6,
        initialCameraRadius: 10,

        legConnections: [],

        currentAction: ACTIONS.NONE,

        user: undefined,
        viewMode: undefined,

        animRotation: undefined,
        animFitviewTarget: undefined,
        animFitviewRadius: undefined,

        dragPosition: BABYLON.Vector3.Zero(),
        scenePointerPosition: BABYLON.Vector3.Zero(),
        screenPointerPosition: BABYLON.Vector2.Zero(),
        cameraTarget: BABYLON.Vector3.Zero(),
        targetRotation: BABYLON.Vector3.Zero(),
        easingFunction: undefined,

        // Created in app.ts at startup
        moduleHelper: undefined,
        entityHelper: undefined,
        ui: undefined,
    };
}
